import {
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, getErrorMessageOrDefault, getErrorResponseMessageOrDefault } from '../../errors/um-errors';
import { ModalComponentProps, User } from '../../types';
import { MedtechRole } from '../../types/medtechRole.types';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';
import { UsergroupsList } from '../../utils/umfe.enums';
import { isBillingUser } from '../../utils/users.utils';
import { updateUsers } from '../../api/medtech-api';

type SetRoleModalProps = ModalComponentProps & {
  getUsers: () => Promise<void>;
  users: User[];
  companyId: number;
};

export const SetRoleModal = ({ closeModal, users, getUsers, companyId }: SetRoleModalProps) => {
  const { t } = useTranslation();
  const [role, setRole] = useState(Object.values(UsergroupsList)[0].value);

  const [isLoading, setLoading] = useState(false);
  const { showToast } = useToastProvider();

  const handleSubmit = (e: any) => {
    setLoading(true);
    e.preventDefault();
    e.stopPropagation();

    const updateUsersPayload = users.map((user: User) => ({
      user_id: user.id,
      is_billing: isBillingUser(user),
      role,
    }));

    updateUsers(companyId, updateUsersPayload)
      .then(async () => {
        await getUsers();
        showToast(
          createSuccessToast(
            t('messages.success.roleChanged', { count: users.length }),
            'users-set-role-success-toast',
          ),
        );
        setLoading(false);
        closeModal();
      })
      .catch((error: unknown) => {
        const errorMessageMap = {
          [ErrorCodes.MINIMUM_QM_USER_REQUIRED]: t('messages.validation.minimumQuality'),
          [ErrorCodes.INVALID_USER_UPDATE]: getErrorResponseMessageOrDefault(error, t('messages.error.updateRole')),
        };

        const errorMessage = getErrorMessageOrDefault(error, errorMessageMap, t('messages.error.updateRole'));

        showToast(createErrorToast(errorMessage, 'users-set-role-error-toast'));
        setLoading(false);
      });
  };

  const setSelectOptions = useMemo(
    () =>
      Object.values(UsergroupsList).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );

  return (
    <QModal onClose={closeModal} size="md" isOpen>
      <QModalHeader>
        <QText data-cy="heading">{t('modals.setRole.title')}</QText>
        <QCloseButton onClick={closeModal} />
      </QModalHeader>
      <QModalBody>
        <QSelect
          data-cy={role}
          aria-label="role"
          value={role}
          onChange={(e) => e && setRole(e.value as MedtechRole)}
          options={setSelectOptions}
        />
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-button" variant="outline" onClick={closeModal}>
          {t('common.cancel')}
        </QButton>
        <QButton data-cy="submit-button" isLoading={isLoading} onClick={handleSubmit}>
          {t('common.submit')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
